import React, { useEffect, useState } from 'react';
import { ArtilleryAPI } from '@Lib/axios/artillery';
import { ArtilleryEmailRecipients } from '@Models/types';
import { Trash, X, MagnifyingGlass, PencilSimple } from '@phosphor-icons/react';
import { MoonLoader } from 'react-spinners';
import Modal from '@Common/Modal';
import showToast from '@Common/toast/showToast';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { useAuth } from '@Context/CognitoAuthProvider';
import ValidateEmail from '@Utils/emailValidation';

const ArtilleryEmailSettings = () => {
  const [emailRecipients, setEmailRecipients] = useState<
    ArtilleryEmailRecipients[]
  >([]);
  const [form, setForm] = useState<{
    recipientType: 'direct' | 'cc' | 'bcc';
    email: string;
  }>({
    recipientType: 'direct',
    email: '',
  });
  const [editingRecipient, setEditingRecipient] =
    useState<ArtilleryEmailRecipients | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    email: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { modals, setModal, loaders, setLoader } = useUtilityContext();
  const { logout } = useAuth();

  useEffect(() => {
    fetchEmailRecipients();
  }, []);

  const fetchEmailRecipients = async () => {
    setLoader('fetchData', true);
    try {
      const artillery = ArtilleryAPI(logout);
      const recipients = await artillery.getEmailRecipients();
      setEmailRecipients(recipients);
    } catch (err) {
      showToast.error('Failed to load email recipients.');
    } finally {
      setLoader('fetchData', false);
    }
  };

  const filteredRecipients = searchTerm
    ? emailRecipients.filter(
        (recipient) =>
          recipient.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          recipient.recipientType
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      )
    : emailRecipients;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const ValidationError = ValidateEmail(form.email);
    if (ValidationError) {
      showToast.error(ValidationError.message);
      return;
    }

    setLoader('submitRecipient', true);

    try {
      const artillery = ArtilleryAPI(logout);

      if (editingRecipient) {
        // Update existing recipient
        await artillery.updateEmailRecipient(editingRecipient.email, {
          recipientType: form.recipientType,
          email: form.email.trim(),
        });
        showToast.success('Email recipient updated successfully!');
        setEditingRecipient(null);
      } else {
        // Create new recipient
        await artillery.createEmailRecipient({
          recipientType: form.recipientType,
          email: form.email.trim(),
        });
        showToast.success('Email recipient created successfully!');
      }

      await fetchEmailRecipients();
      setForm({ recipientType: 'direct', email: '' });
    } catch (err) {
      showToast.error(
        editingRecipient
          ? 'Failed to update email recipient.'
          : 'Failed to create email recipient.'
      );
    } finally {
      setLoader('submitRecipient', false);
    }
  };

  const handleEdit = (recipient: ArtilleryEmailRecipients) => {
    setEditingRecipient(recipient);
    setForm({
      recipientType: recipient.recipientType,
      email: recipient.email,
    });
  };

  const handleCancelEdit = () => {
    setEditingRecipient(null);
    setForm({ recipientType: 'direct', email: '' });
  };

  const handleDelete = (recipient: ArtilleryEmailRecipients) => {
    setDeleteTarget({
      email: recipient.email,
    });
    setModal('deleteRecipient', true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setLoader('deleteRecipient', true);
    try {
      const artillery = ArtilleryAPI(logout);
      await artillery.deleteEmailRecipient(deleteTarget.email);
      showToast.success('Email recipient deleted successfully!');
      await fetchEmailRecipients();
    } catch (err) {
      showToast.error('Failed to delete email recipient.');
    } finally {
      setModal('deleteRecipient', false);
      setDeleteTarget(null);
      setLoader('deleteRecipient', false);
    }
  };

  const getRecipientTypeColor = (type: string) => {
    switch (type) {
      case 'direct':
        return 'bg-blue-100 text-blue-800';
      case 'cc':
        return 'bg-green-100 text-green-800';
      case 'bcc':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecipientTypeLabel = (type: string) => {
    switch (type) {
      case 'direct':
        return 'Direct';
      case 'cc':
        return 'CC';
      case 'bcc':
        return 'BCC';
      default:
        return type;
    }
  };

  return (
    <div className="flex h-full w-full flex-col gap-3 p-2">
      <div className="flex flex-col">
        <h2 className="text-xl font-semibold text-blue-900">
          Artillery Email Recipients
        </h2>
        <p className="text-sm text-blue-900">
          Manage email recipients who will receive notifications when Artillery
          reports are generated. Configure direct recipients, CC, and BCC as
          needed.
        </p>
      </div>

      <div className="flex h-full w-full flex-row gap-2">
        {/* FORM */}
        <div className="flex h-full w-1/3 flex-col rounded-md border border-gray-400 bg-white p-4">
          <span className="font-semibold">
            {editingRecipient ? 'Edit Email Recipient' : 'Add Email Recipient'}
          </span>

          <form
            onSubmit={handleSubmit}
            className="flex h-full flex-col gap-y-2"
          >
            <div>
              <label className="text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter email address"
                value={form.email}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
                maxLength={55}
                disabled={loaders.submitRecipient}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">
                Recipient Type
              </label>
              <select
                name="recipientType"
                value={form.recipientType}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                required
                disabled={loaders.submitRecipient}
              >
                <option value="direct">Direct (To)</option>
                <option value="cc">CC (Carbon Copy)</option>
                <option value="bcc">BCC (Blind Carbon Copy)</option>
              </select>
            </div>

            <div className="flex flex-grow items-end justify-between gap-2">
              {editingRecipient && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={loaders.submitRecipient}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="flex flex-grow items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={loaders.submitRecipient}
              >
                {loaders.submitRecipient ? (
                  <>
                    <MoonLoader color="#ffffff" size={16} className="mr-2" />
                    {editingRecipient ? 'Updating...' : 'Adding...'}
                  </>
                ) : editingRecipient ? (
                  'Update Recipient'
                ) : (
                  'Add Recipient'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* LIST */}
        <div className="flex h-full w-2/3 flex-col rounded-md border border-gray-400 bg-white p-4">
          <div className="flex flex-col gap-4">
            <span className="font-semibold">Email Recipients</span>

            {/* Search Input */}
            <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-4 py-2">
              <MagnifyingGlass size={20} weight="bold" />
              <input
                type="text"
                className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                placeholder="Search recipients by email or type"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <span
                  className="cursor-pointer hover:text-brandRed-500"
                  onClick={() => setSearchTerm('')}
                >
                  <X weight="bold" />
                </span>
              )}
            </div>
          </div>

          {loaders.fetchData ? (
            <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
              <MoonLoader color="#071D49" size={100} />
              <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
                Fetching Email Recipients
              </span>
            </span>
          ) : filteredRecipients.length === 0 ? (
            <div className="p-4 italic">
              {searchTerm
                ? 'No recipients match your search'
                : 'No email recipients found. Add your first recipient.'}
            </div>
          ) : (
            <div className="custom-scroll flex h-0 flex-grow flex-col gap-y-2 overflow-auto overflow-y-auto p-2">
              {filteredRecipients.map((recipient) => (
                <div
                  key={recipient._id}
                  className="flex w-full flex-row items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-col">
                    <h4 className="font-medium text-gray-900">
                      {recipient.email}
                    </h4>
                    <span
                      className={`inline-flex w-fit rounded-full px-2 py-1 text-xs font-medium ${getRecipientTypeColor(
                        recipient.recipientType
                      )}`}
                    >
                      {getRecipientTypeLabel(recipient.recipientType)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(recipient)}
                      className="flex rounded p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50"
                      title="Edit recipient"
                      disabled={
                        loaders.submitRecipient || loaders.deleteRecipient
                      }
                    >
                      <PencilSimple size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(recipient)}
                      className="flex rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50"
                      title="Delete recipient"
                      disabled={
                        loaders.submitRecipient || loaders.deleteRecipient
                      }
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modals.deleteRecipient}
        onClose={() => {
          setModal('deleteRecipient', false);
          setDeleteTarget(null);
        }}
        title="Confirm Delete Email Recipient"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete the email recipient{' '}
            <span className="font-semibold">{deleteTarget?.email}</span>?
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setModal('deleteRecipient', false);
                setDeleteTarget(null);
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loaders.deleteRecipient}
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              disabled={loaders.deleteRecipient}
            >
              {loaders.deleteRecipient ? (
                <>
                  <MoonLoader color="#ffffff" size={16} className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ArtilleryEmailSettings;
